import ProposalLinkAInternalPreviewBanner from "@/components/proposal/ProposalLinkAInternalPreviewBanner";
import ProposalStickyClarityLogo from "@/components/proposal/ProposalStickyClarityLogo";
import ProposalSupersededPage from "@/components/proposal/ProposalSupersededPage";
import RetailPremiumProposalContent from "@/components/proposal/RetailPremiumProposalContent";
import DtoolsLiveProposalContent from "@/components/sales/dtools/DtoolsLiveProposalContent";
import ProposalApprovedVariationOrders from "@/components/proposals/ProposalApprovedVariationOrders";
import { PROPOSAL_ACCENT, PROPOSAL_BG, PROPOSAL_COPY_FONT, PROPOSAL_TEXT, proposalMoney } from "@/lib/proposalTheme";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Copy, Mail, MessageCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { buildArchivedProposalWhatsAppHref } from "@/lib/proposalCustomerActions";
import { staffPrimaryProposalViewPath, clearStaffViewPdfQueryFromLocation, isStaffViewPdfRequested } from "@/lib/staffProposalViewPaths";
import ProposalPdfDownloadButton from "@/components/proposal/ProposalPdfDownloadButton";
import ProposalPublicCleoAssistant from "@/components/proposal/ProposalPublicCleoAssistant";
import {
  isProjectBackedProposalSourceValue,
  PROJECT_PROPOSAL_SOURCE,
  RETAIL_PROPOSAL_SOURCE,
  isManualPdfUploadProposalSourceValue,
  normalisedProposalSource,
  isDtoolsJsonProposalSourceValue,
} from "@shared/proposalSources";
import { canCreateProposalRevision } from "@shared/commercialHead";
import {
  buildDtoolsLiveProposalViewModel,
  resolveProposalContentLayout,
} from "@shared/dtoolsProposalLiveView";
import DtoolsLiveProposalUnavailable from "@/components/sales/dtools/DtoolsLiveProposalUnavailable";
import ProposalDraftPromotionDialog from "@/components/sales/ProposalDraftPromotionDialog";
import ProposalCustomerLinkShareField, {
  copyProposalCustomerLink,
} from "@/components/sales/ProposalCustomerLinkShareField";
import ProposalPipelineStageBadge from "@/components/sales/ProposalPipelineStageBadge";
import { isPipelineDraftStage } from "@shared/proposalPipelineStage";
import { isStaffLinkAProposalPreviewPath } from "@shared/proposalPublicRoutes";
import { readDtoolsDisplaySettings } from "@shared/dtoolsDisplaySettings";
import { resolveCustomerFacingPaymentTerms } from "@shared/proposalCustomerPaymentTerms";
import { PROPOSAL_CANCELLED_CUSTOMER_MESSAGE } from "@shared/proposalCancel";
import {
  isProposalCustomerCleoContextHidden,
  shouldShowProposalCleoAssistantUi,
  type ProposalCleoAudience,
} from "@/lib/proposalCustomerAiUxFlags";
import type { ProposalCleoPendingAsk } from "@shared/proposalCleoInProposalExplain";
import { resolveRetailProposalTitle } from "@shared/retailProposalPremiumView";
import {
  resolveProposalCustomerPricingDisplay,
  resolveRetailPricesVisibleToCustomer,
} from "@shared/proposalCustomerPricing";
import { resolveDtoolsHierarchyProposalTotalNumeric } from "@shared/dtoolsSystemSummary";

/** Narrow shape for revision diff maps (Link A staff preview only). */
type ProposalLineForDiff = {
  descriptionSnapshot: string;
  lineTotal: unknown;
  quantity: unknown;
};

const money = proposalMoney;
const SALES_BG = PROPOSAL_BG;
const SALES_TEXT = PROPOSAL_TEXT;
const SALES_ACCENT = PROPOSAL_ACCENT;
const COPY_FONT = PROPOSAL_COPY_FONT;

export default function ProposalPublicPage() {
  const params = useParams<{ proposalId?: string; id?: string }>();
  const [path, setLocation] = useLocation();
  const id = Number(params.proposalId ?? params.id ?? 0);
  const isInternalPreview = isStaffLinkAProposalPreviewPath(path);
  /** Clarity One authenticated view — same renderer, ownership-scoped loader. */
  const isClarityOneCustomerView = path.startsWith("/proposals/");
  const [staffViewPdfRequested, setStaffViewPdfRequested] = useState(() =>
    typeof window !== "undefined" ? isStaffViewPdfRequested(window.location.search) : false,
  );
  const [cleoOpen, setCleoOpen] = useState(false);
  const [cleoPendingAsk, setCleoPendingAsk] = useState<ProposalCleoPendingAsk | null>(null);
  const [sendDone, setSendDone] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [promotionOpen, setPromotionOpen] = useState(false);
  const [liveCustomerPublicUrl, setLiveCustomerPublicUrl] = useState("");
  const pendingShareRef = useRef<null | ((url: string) => Promise<void>)>(null);
  /** Clarity1 + Link B are customer audience; Link A preview is staff. */
  const cleoAudience: ProposalCleoAudience = isInternalPreview ? "staff" : "customer";

  const publicQ = trpc.proposals.getPublicProposalById.useQuery(
    { id },
    { enabled: Number.isFinite(id) && id > 0 && !isInternalPreview && !isClarityOneCustomerView, retry: false }
  );
  const clarityOneQ = trpc.clarityOneRecords.getProposal.useQuery(
    { proposalId: id },
    { enabled: Number.isFinite(id) && id > 0 && isClarityOneCustomerView, retry: false }
  );
  const previewQ = trpc.proposals.getProposalPreviewById.useQuery(
    { id },
    {
      enabled: Number.isFinite(id) && id > 0 && isInternalPreview,
      retry: false,
      refetchInterval: (query) => {
        const pending = Number((query.state.data as { cleoDescriptionsPending?: number } | undefined)?.cleoDescriptionsPending ?? 0);
        return isInternalPreview && pending > 0 ? 5000 : false;
      },
    },
  );
  // Staff preview only: authoritative secure Link B URL (null until an active credential has
  // been issued, e.g. still draft). Never derive this from the header alone — that throws.
  const linkBStatusQ = trpc.proposals.getProposalLinkBCredentialStatus.useQuery(
    { proposalId: id },
    { enabled: isInternalPreview && Number.isFinite(id) && id > 0, retry: false },
  );
  const scheduleCleoDescriptions = trpc.proposals.scheduleProposalCleoDescriptions.useMutation();
  const scheduledCleoForProposalRef = useRef<number | null>(null);
  // Shared authority: Link B and Clarity One return the same payload from loadCustomerFacingProposal.
  // Narrowing to a content payload happens via expired/cancelled/archived/superseded gates below.
  const data = (
    isInternalPreview
      ? previewQ.data
      : isClarityOneCustomerView
        ? clarityOneQ.data
        : publicQ.data
  ) as any;
  const isLoading = isInternalPreview
    ? previewQ.isLoading
    : isClarityOneCustomerView
      ? clarityOneQ.isLoading
      : publicQ.isLoading;
  const refetch = isInternalPreview
    ? previewQ.refetch
    : isClarityOneCustomerView
      ? clarityOneQ.refetch
      : publicQ.refetch;

  // Link A only: soft “newer revision” banner + diff (customers get hard-block page instead).
  const { data: parentPreviewProposal } = trpc.proposals.getProposalPreviewById.useQuery(
    { id: data?.header?.parentProposalId ?? 0 },
    { enabled: !!data?.header?.parentProposalId && isInternalPreview },
  );

  const markSent = trpc.proposals.markSentForCustomer.useMutation({
    onSuccess: async (res) => {
      if (res && "needsDraftPromotion" in res && res.needsDraftPromotion) return;
      setSendDone(true);
      await refetch();
      toast.success("Customer approval link is now live.");
      const customerTotalWarning = res && "customerTotalWarning" in res ? res.customerTotalWarning : null;
      if (customerTotalWarning) toast.warning(customerTotalWarning, { duration: 12_000 });
    },
    onError: (e) => toast.error(e.message || "Failed to send proposal to customer."),
  });

  useEffect(() => {
    scheduledCleoForProposalRef.current = null;
  }, [id]);

  useEffect(() => {
    if (!isInternalPreview || !Number.isFinite(id) || id <= 0 || !previewQ.data) return;
    const pending = Number((previewQ.data as { cleoDescriptionsPending?: number }).cleoDescriptionsPending ?? 0);
    if (pending <= 0) return;
    const source = (previewQ.data.header as { proposalSource?: string | null } | undefined)?.proposalSource;
    if (!isDtoolsJsonProposalSourceValue(source)) return;
    if (scheduledCleoForProposalRef.current === id) return;
    scheduledCleoForProposalRef.current = id;
    scheduleCleoDescriptions.mutate({ proposalId: id });
  }, [isInternalPreview, id, previewQ.data, scheduleCleoDescriptions]);

  const latestRevisionId = useMemo(
    () => (isInternalPreview ? (data?.latestRevisionId ?? null) : null),
    [isInternalPreview, data?.latestRevisionId],
  );
  const revisionDiff = useMemo(() => {
    if (!isInternalPreview || !parentPreviewProposal?.items?.length || !data?.items?.length) return null;
    const prevItems = parentPreviewProposal.items as ProposalLineForDiff[];
    const nextItems = data.items as ProposalLineForDiff[];
    const prev = new Map<string, ProposalLineForDiff>(
      prevItems.map((i) => [i.descriptionSnapshot.trim().toLowerCase(), i]),
    );
    const next = new Map<string, ProposalLineForDiff>(
      nextItems.map((i) => [i.descriptionSnapshot.trim().toLowerCase(), i]),
    );
    let added = 0;
    let removed = 0;
    let changed = 0;
    for (const [key, item] of Array.from(next.entries())) {
      const old = prev.get(key);
      if (!old) {
        added += 1;
      } else if (Number(old.lineTotal) !== Number(item.lineTotal) || Number(old.quantity) !== Number(item.quantity)) {
        changed += 1;
      }
    }
    for (const key of Array.from(prev.keys())) {
      if (!next.has(key)) removed += 1;
    }
    return { added, removed, changed };
  }, [isInternalPreview, parentPreviewProposal?.items, data?.items]);

  const items = data?.items ?? [];
  const proposalContentLayout = useMemo(() => {
    if (!data?.header) return "standard" as const;
    return resolveProposalContentLayout(
      {
        proposalSource: data.header.proposalSource,
        parsedMetadataJson: (data.header as { parsedMetadataJson?: unknown }).parsedMetadataJson,
      },
      items as Array<{
        id: number;
        rawText?: string | null;
        descriptionSnapshot?: string | null;
        name?: string | null;
      }>,
    );
  }, [data?.header, items]);
  const dtoolsLiveViewModel = useMemo(() => {
    if (!data?.header || proposalContentLayout !== "dtools_beautified") return null;
    try {
      const variationOrderScope =
        (data as { variationOrderScope?: import("@shared/variationOrderCurrentScope").ComposedProjectScope | null })
          .variationOrderScope ?? null;
      const systemOverviewAnnotations =
        (data as {
          systemOverviewAnnotations?: Record<
            string,
            import("@shared/variationOrderSystemOverview").VoSystemOverviewAnnotation
          >;
        }).systemOverviewAnnotations ?? {};
      return buildDtoolsLiveProposalViewModel(
        {
          proposalSource: data.header.proposalSource,
          proposalName: data.header.proposalName,
          proposalRef: data.header.proposalRef,
          totalAmount: data.header.totalAmount,
          createdAt: data.header.createdAt,
          paymentTermsJson: (data.header as { paymentTermsJson?: unknown }).paymentTermsJson,
          parsedMetadataJson: (data.header as { parsedMetadataJson?: unknown }).parsedMetadataJson,
          customerNameSnapshot: (data.header as { customerNameSnapshot?: string | null }).customerNameSnapshot,
          projectNumber: (data.header as { projectNumber?: string | null }).projectNumber,
        },
        items as Array<{
          id: number;
          name?: string | null;
          brand?: string | null;
          model?: string | null;
          category?: string | null;
          rawText?: string | null;
          descriptionSnapshot?: string | null;
          imageUrlSnapshot?: string | null;
          resolvedImageUrl?: string | null;
          quantity?: number | null;
          unitPriceSnapshot?: number | null;
          lineTotal?: number | null;
          sortOrder?: number | null;
          cleoDescription?: string | null;
          customerExplanation?: string | null;
        }>,
        { variationOrderScope, systemOverviewAnnotations },
      );
    } catch {
      return null;
    }
  }, [data, items, proposalContentLayout]);
  const isDtoolsJsonProposal = useMemo(
    () => isDtoolsJsonProposalSourceValue(data?.header?.proposalSource),
    [data?.header?.proposalSource],
  );
  const useDtoolsBeautifiedLayout = proposalContentLayout === "dtools_beautified" && dtoolsLiveViewModel != null;
  const dtoolsBeautifiedUnavailable = isDtoolsJsonProposal && !useDtoolsBeautifiedLayout;
  const isPremiumStandardLayout = !isDtoolsJsonProposal && proposalContentLayout !== "dtools_beautified";
  const isCatalogueRetailProposal = useMemo(() => {
    if (!data?.header) return false;
    const h = data.header as {
      proposalSource?: string | null;
      originalProposalStorageKey?: string | null;
      projectId?: number | null;
    };
    if (String(h.originalProposalStorageKey ?? "").trim()) return false;
    const pid = h.projectId != null ? Number(h.projectId) : NaN;
    if (Number.isFinite(pid) && pid > 0) return false;
    if (isProjectBackedProposalSourceValue(h.proposalSource)) return false;
    const norm = normalisedProposalSource(h.proposalSource);
    return norm === RETAIL_PROPOSAL_SOURCE;
  }, [data?.header]);
  /** Customer Cleo Ask is on when the code gate is true; Link A staff stays catalogue-retail-only. */
  const showCleoAssistantUi = shouldShowProposalCleoAssistantUi({
    audience: cleoAudience,
    isCatalogueRetailProposal,
  });

  useEffect(() => {
    if (!showCleoAssistantUi) {
      setCleoOpen(false);
      setCleoPendingAsk(null);
    }
  }, [showCleoAssistantUi]);
  const retailPricing = (data?.header?.retailPricingJson as
    | {
        listTotal?: number;
        originalListPrice?: number;
        agreedPriceSgd?: number;
        agreedPrice?: number;
        pricesVisibleToCustomer?: boolean;
      }
    | null
    | undefined) ?? null;
  const dtoolsHierarchyProposalTotal = useMemo(() => {
    if (!dtoolsLiveViewModel) return null;
    return resolveDtoolsHierarchyProposalTotalNumeric({
      systems: dtoolsLiveViewModel.systems,
      footerAdjustments: dtoolsLiveViewModel.footer.adjustments,
      reconciliation: dtoolsLiveViewModel.parseResult.reconciliation,
      displaySettings: dtoolsLiveViewModel.displaySettings,
    });
  }, [dtoolsLiveViewModel]);
  const customerPricing = resolveProposalCustomerPricingDisplay({
    totalAmount:
      dtoolsLiveViewModel?.variationOrderScope?.revisedContractValue ??
      dtoolsHierarchyProposalTotal ??
      data?.header?.totalAmount,
    retailPricingJson: retailPricing,
    invoiceGstEnabled: (data?.header as { invoiceGstEnabled?: boolean | null } | undefined)?.invoiceGstEnabled,
    computedProposalTotal:
      dtoolsLiveViewModel?.variationOrderScope?.revisedContractValue ?? dtoolsHierarchyProposalTotal,
    lineItemTotalSum:
      dtoolsLiveViewModel?.variationOrderScope?.revisedContractValue ?? dtoolsHierarchyProposalTotal,
    snapshotProposalTotal:
      dtoolsLiveViewModel?.variationOrderScope?.revisedContractValue ?? dtoolsHierarchyProposalTotal,
  });
  const parsedMetadataJson = (data?.header as { parsedMetadataJson?: unknown } | undefined)?.parsedMetadataJson;
  const paymentTerms = resolveCustomerFacingPaymentTerms({
    paymentTermsJson: (data?.header as { paymentTermsJson?: unknown } | undefined)?.paymentTermsJson,
    displaySettings: parsedMetadataJson ? readDtoolsDisplaySettings(parsedMetadataJson) : undefined,
  });
  const customerEmail = data?.customer?.email?.trim() ?? "";
  const customerPhoneRaw = data?.customer?.contact?.trim() ?? "";
  const customerPhoneDigits = customerPhoneRaw.replace(/[^\d+]/g, "");
  const customerPublicUrl = linkBStatusQ.data?.publicUrl ?? "";

  const pipelineStage = (data?.header as { pipelineStage?: string | null } | undefined)?.pipelineStage ?? null;
  const needsDraftPromotion =
    isInternalPreview &&
    Boolean(data?.header) &&
    data.header.status === "draft" &&
    isPipelineDraftStage(pipelineStage) &&
    !sendDone;

  const resolveShareUrl = () => liveCustomerPublicUrl || customerPublicUrl;

  const ensureCustomerLinkReady = async (confirmDraftPromotion = false): Promise<string | false> => {
    if (!data?.header) return false;
    try {
      const res = await markSent.mutateAsync({
        proposalId: data.header.id,
        confirmDraftPromotion,
      });
      if (res && "needsDraftPromotion" in res && res.needsDraftPromotion) {
        return false;
      }
      if (res && "ok" in res && res.ok) {
        const authoritativeUrl = String((res as { publicUrl?: string | null }).publicUrl ?? "").trim();
        const linkBPath = String((res as { linkB?: string | null }).linkB ?? "").trim();
        const nextUrl =
          authoritativeUrl ||
          (linkBPath
            ? `${window.location.origin.replace(/\/+$/, "")}${linkBPath.startsWith("/") ? linkBPath : `/${linkBPath}`}`
            : resolveShareUrl());
        if (nextUrl) setLiveCustomerPublicUrl(nextUrl);
        return nextUrl || resolveShareUrl();
      }
      return resolveShareUrl();
    } catch {
      return false;
    }
  };

  const runShareWithPromotionGate = async (action: (url: string) => Promise<void>) => {
    if (needsDraftPromotion) {
      pendingShareRef.current = action;
      setPromotionOpen(true);
      return;
    }
    const url = await ensureCustomerLinkReady(false);
    if (!url) {
      pendingShareRef.current = action;
      setPromotionOpen(true);
      return;
    }
    await action(url);
    setSendModalOpen(false);
  };

  const copyCustomerLink = async () => {
    await runShareWithPromotionGate(async (url) => {
      await copyProposalCustomerLink(url);
    });
  };

  const sendViaWhatsApp = async () => {
    if (!customerPhoneDigits) return;
    await runShareWithPromotionGate(async (url) => {
      const msg = `Hi, here's your proposal from Clarity: ${url}`;
      const waHref = `https://wa.me/${encodeURIComponent(customerPhoneDigits)}?text=${encodeURIComponent(msg)}`;
      window.open(waHref, "_blank", "noopener");
    });
  };

  const sendViaEmail = async () => {
    if (!customerEmail) return;
    await runShareWithPromotionGate(async (url) => {
      const subject = "Your Clarity proposal";
      const body = `Hi,\n\nPlease review your proposal here:\n${url}\n\nThank you.`;
      window.open(
        `mailto:${encodeURIComponent(customerEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
        "_blank",
        "noopener",
      );
    });
  };

  /** Uploaded D-Tools PDFs: wrapper-only — never render structured proposal UI here. */
  useEffect(() => {
    if (!data?.header) return;
    const uploadKey = String((data.header as { originalProposalStorageKey?: string | null }).originalProposalStorageKey ?? "").trim();
    if (!uploadKey) return;
    if (isManualPdfUploadProposalSourceValue(data.header.proposalSource)) {
      setLocation(
        isInternalPreview
          ? `/sales/proposals/${data.header.id}/preview-manual`
          : `/proposal-manual/${data.header.id}`,
        { replace: true }
      );
      return;
    }
    if (isDtoolsJsonProposalSourceValue(data.header.proposalSource)) {
      return;
    }
    const wrapPid = data.header.projectId != null ? Number(data.header.projectId) : NaN;
    if (!Number.isFinite(wrapPid) || wrapPid <= 0) return;
    setLocation(`/proposal-wrapper/${wrapPid}/${data.header.id}`, { replace: true });
  }, [data?.header, setLocation, isInternalPreview]);

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: SALES_BG, color: SALES_TEXT }}>Loading proposal...</div>;
  }

  if (!isInternalPreview && data && "expired" in data && data.expired) {
    const ex = data;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" style={{ background: SALES_BG, color: SALES_TEXT }}>
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-light" style={{ fontFamily: COPY_FONT }}>
            This proposal has expired.
          </h1>
          <p className="text-sm text-[#1A2744]/80 leading-relaxed" style={{ fontFamily: COPY_FONT }}>
            Please contact Clarity if you would like us to renew access.
          </p>
          {ex.proposalRef ? (
            <p className="text-xs tracking-wider text-[#1A2744]/50">Reference: {ex.proposalRef}</p>
          ) : null}
        </div>
      </div>
    );
  }

  if (!isInternalPreview && data && "superseded" in data && data.superseded) {
    return (
      <ProposalSupersededPage
        proposalRef={data.proposalRef}
        latestProposalRef={data.latestProposalRef ?? null}
        latestProposalPath={data.latestProposalPath ?? null}
        salespersonName={data.salesperson?.name ?? null}
      />
    );
  }

  if (!isInternalPreview && data && "cancelled" in data && data.cancelled) {
    const cx = data;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" style={{ background: SALES_BG, color: SALES_TEXT }}>
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-light" style={{ fontFamily: COPY_FONT }}>
            This proposal is no longer available.
          </h1>
          <p className="text-sm text-[#1A2744]/80 leading-relaxed" style={{ fontFamily: COPY_FONT }}>
            {PROPOSAL_CANCELLED_CUSTOMER_MESSAGE}
          </p>
          {cx.proposalRef ? (
            <p className="text-xs tracking-wider text-[#1A2744]/50">Reference: {cx.proposalRef}</p>
          ) : null}
        </div>
      </div>
    );
  }

  if (!isInternalPreview && data && "archived" in data && data.archived) {
    const ax = data;
    const spName = ax.salesperson.name?.trim() || "your Clarity consultant";
    const photoUrl = ax.salesperson.photoUrl?.trim() || null;
    const phoneDisplay = (ax.salesperson.phone ?? "").trim();
    const waHref = buildArchivedProposalWhatsAppHref({
      salespersonName: ax.salesperson.name,
      proposalRef: ax.proposalRef ?? "",
      contact: ax.salesperson.phone,
    });
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" style={{ background: SALES_BG, color: SALES_TEXT }}>
        <div className="max-w-md text-center space-y-5">
          <h1 className="text-2xl font-light" style={{ fontFamily: COPY_FONT }}>
            This Proposal Is No Longer Valid
          </h1>
          <p className="text-sm text-[#1A2744]/80 leading-relaxed" style={{ fontFamily: COPY_FONT }}>
            This proposal has been archived and is no longer available for review.
          </p>
          <p className="text-sm text-[#1A2744]/80 leading-relaxed" style={{ fontFamily: COPY_FONT }}>
            If you would like to revisit this project, please contact your Clarity consultant.
          </p>
          {ax.proposalRef ? (
            <p className="text-xs tracking-wider text-[#1A2744]/50">Reference: {ax.proposalRef}</p>
          ) : null}
          <div className="flex flex-col items-center gap-2 pt-2">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={spName}
                className="h-16 w-16 rounded-full object-cover border border-[#1A2744]/15"
              />
            ) : null}
            <p className="text-sm font-medium" style={{ fontFamily: COPY_FONT }}>
              {spName}
            </p>
            {phoneDisplay ? (
              <p className="text-xs text-[#1A2744]/60">{phoneDisplay}</p>
            ) : null}
          </div>
          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-md px-5 py-2.5 text-sm font-medium text-white"
              style={{ background: SALES_TEXT }}
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Contact via WhatsApp
            </a>
          ) : (
            <p className="text-xs text-[#1A2744]/60">
              WhatsApp is unavailable for this consultant. Please contact Clarity directly.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!data?.header) {
    const previewErr = isInternalPreview ? previewQ.error?.message : null;
    const publicErr = !isInternalPreview ? publicQ.error?.message : null;
    const hint =
      !isInternalPreview &&
      ((isClarityOneCustomerView && clarityOneQ.isSuccess && !clarityOneQ.data) ||
        (!isClarityOneCustomerView && publicQ.isSuccess && !publicQ.data))
        ? "This proposal may still be in draft. Staff: open the internal preview from the deal workspace."
        : null;
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: SALES_BG, color: SALES_TEXT }}>
        <div className="max-w-md text-center space-y-2 text-sm" style={{ fontFamily: COPY_FONT }}>
          <p>Proposal not found.</p>
          {hint ? <p className="text-xs text-[#1A2744]/70">{hint}</p> : null}
          {previewErr || publicErr ? (
            <p className="text-xs text-[#1A2744]/50">{previewErr ?? publicErr}</p>
          ) : null}
        </div>
      </div>
    );
  }

  const hasUploadedOriginalPdf = Boolean(
    String((data.header as { originalProposalStorageKey?: string | null }).originalProposalStorageKey ?? "").trim()
  );
  const isManualUploadedProposal = isManualPdfUploadProposalSourceValue(data.header.proposalSource);
  const wrapperProjectId = data.header.projectId != null ? Number(data.header.projectId) : NaN;

  if (hasUploadedOriginalPdf && isManualUploadedProposal) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: SALES_BG, color: SALES_TEXT }}>
        <p className="text-sm text-center" style={{ fontFamily: COPY_FONT }}>
          Opening your proposal…
        </p>
      </div>
    );
  }

  if (hasUploadedOriginalPdf && Number.isFinite(wrapperProjectId) && wrapperProjectId > 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: SALES_BG, color: SALES_TEXT }}>
        <p className="text-sm text-center" style={{ fontFamily: COPY_FONT }}>
          Opening your proposal…
        </p>
      </div>
    );
  }

  if (hasUploadedOriginalPdf) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" style={{ background: SALES_BG, color: SALES_TEXT }}>
        <p className="text-sm text-center max-w-md" style={{ fontFamily: COPY_FONT }}>
          This uploaded proposal is missing project linkage. Please contact your salesperson.
        </p>
      </div>
    );
  }

  const customerFullViewportCover =
    !isInternalPreview || useDtoolsBeautifiedLayout || isPremiumStandardLayout;
  const hideClarityIntroduction = Boolean(
    readDtoolsDisplaySettings(parsedMetadataJson).hideClarityIntroduction,
  );
  const isApprovedReadOnly = !isInternalPreview && data.header.status === "approved";
  const showCancelledAcceptedBanner = Boolean(
    !isInternalPreview && (data as { showCancelledBanner?: boolean }).showCancelledBanner,
  );
  const approvedReadOnlyBanner = isApprovedReadOnly ? (
    <div
      className="rounded-md border px-4 py-3 text-sm"
      style={{ borderColor: SALES_ACCENT, background: "rgba(200,169,106,0.1)" }}
    >
      This proposal has been approved. You are viewing your confirmed copy in read-only mode.
    </div>
  ) : null;
  const cancelledAcceptedBanner = showCancelledAcceptedBanner ? (
    <div
      className="rounded-md border px-4 py-3 text-sm"
      style={{ borderColor: "#B45309", background: "rgba(180,83,9,0.08)" }}
    >
      This accepted proposal remains available for your records. The related project has since been cancelled or closed.
    </div>
  ) : null;
  const revisionBanner =
    isInternalPreview &&
    data.header.parentProposalId &&
    latestRevisionId &&
    canCreateProposalRevision(data.header) ? (
      <div
        className="rounded-md border px-4 py-3 text-sm"
        style={{ borderColor: SALES_ACCENT, background: "rgba(200,169,106,0.1)" }}
      >
        This proposal has been revised.{" "}
        <a href={`/sales/proposals/${latestRevisionId}/preview`} className="underline font-medium">
          Click here to view the latest version.
        </a>
        {revisionDiff ? (
          <p className="mt-2 text-xs opacity-80">
            Diff: +{revisionDiff.added} added, -{revisionDiff.removed} removed, {revisionDiff.changed} changed.
          </p>
        ) : null}
      </div>
    ) : null;

  const showProposalStickyLogo = customerFullViewportCover;

  return (
    <div
      className={`min-h-screen proposal-customer-page${useDtoolsBeautifiedLayout ? " dtools-beautified-proposal" : ""}`}
      style={{ background: SALES_BG, color: SALES_TEXT }}
    >
      <style>{`
        .dtools-proposal-pdf-package-screen-hidden {
          position: fixed;
          left: -10000px;
          top: 0;
          width: 794px;
          visibility: hidden;
          pointer-events: none;
          overflow: hidden;
          max-height: 1px;
        }
        .dtools-proposal-pdf-package-print-ready {
          position: fixed;
          left: -10000px;
          top: 0;
          width: 794px;
          visibility: visible;
          pointer-events: none;
          overflow: visible;
          max-height: none;
        }
        .dtools-proposal-pdf-package .dtools-pdf-quotation-page-shell,
        .dtools-proposal-pdf-package .pdf-print-page.dtools-pdf-quotation-page-shell {
          box-sizing: border-box;
          width: 794px;
          min-height: 1123px;
        }
        .dtools-proposal-pdf-package .dtools-pdf-quotation-logo,
        .dtools-proposal-pdf-package .dtools-pdf-closing-logo,
        .dtools-proposal-pdf-package .pdf-print-page-logo {
          display: block;
          width: 88px;
          max-width: 95px;
          height: auto;
          object-fit: contain;
          flex: none;
        }
        .dtools-proposal-pdf-package .dtools-pdf-closing-logo {
          width: 110px;
          max-width: 120px;
        }
        @media print {
          html, body {
            height: auto !important;
            overflow: visible !important;
          }
          .proposal-no-print { display: none !important; }
          .proposal-customer-page { background: white !important; }
          .dtools-beautified-proposal [data-testid="proposal-cover-fullscreen"] { display: none !important; }
          .dtools-beautified-proposal .dtools-proposal-screen-only { display: none !important; }
          .dtools-beautified-proposal .dtools-proposal-pdf-package,
          .dtools-beautified-proposal .dtools-proposal-pdf-package-screen-hidden,
          .dtools-beautified-proposal .dtools-proposal-pdf-package-print-ready {
            display: block !important;
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: auto !important;
            visibility: visible !important;
            pointer-events: auto !important;
            overflow: visible !important;
            max-height: none !important;
            transform: none !important;
            filter: none !important;
            backdrop-filter: none !important;
          }
          .dtools-proposal-pdf-package .pdf-keep-together,
          .dtools-proposal-pdf-package .pdf-keep-together > *,
          .dtools-proposal-pdf-package article,
          .dtools-proposal-pdf-package [data-testid^="dtools-system-included-card"],
          .dtools-proposal-pdf-package [data-testid="proposal-consolidated-system-summary"] tr,
          .dtools-proposal-pdf-package [data-testid^="dtools-pdf-line-row-"],
          .dtools-proposal-pdf-package [data-testid="dtools-pdf-primary-product-card"],
          .dtools-proposal-pdf-package .rounded-xl.border,
          .dtools-proposal-pdf-package [data-testid="customer-system-page-total"],
          .dtools-proposal-pdf-package [data-testid="dtools-pdf-system-value"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .dtools-proposal-pdf-package [data-testid="dtools-pdf-cover-page"] {
            overflow: visible !important;
          }
          .dtools-proposal-pdf-package .pdf-page-break-after {
            break-after: page;
            page-break-after: always;
          }
          .dtools-proposal-pdf-package .pdf-pagination-source-hidden {
            display: none !important;
          }
          .dtools-proposal-pdf-package .pdf-print-page {
            break-after: page;
            page-break-after: always;
          }
          .dtools-proposal-pdf-package .pdf-print-page-last,
          .dtools-proposal-pdf-package [data-testid="dtools-pdf-closing-page"] {
            break-after: auto !important;
            page-break-after: auto !important;
          }
          .dtools-proposal-pdf-package .dtools-pdf-compact-table thead {
            display: table-header-group;
          }
          .dtools-proposal-pdf-package .pdf-print-page-body {
            padding-top: 0;
          }
          @page {
            size: A4 portrait;
            margin: 10mm 8mm;
          }
          .dtools-proposal-pdf-package .pdf-print-page-header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 8mm;
            min-height: 10mm;
          }
          .dtools-proposal-pdf-package .pdf-print-page-title {
            flex: 1 1 auto;
            margin: 0;
            font-size: 11pt;
            font-weight: 600;
            line-height: 1.3;
            color: #1A2744;
          }
          .dtools-proposal-pdf-package .pdf-print-page-logo {
            flex: 0 0 auto;
            height: 8mm;
            width: auto;
            object-fit: contain;
          }
        }
      `}</style>
      {showProposalStickyLogo ? <ProposalStickyClarityLogo /> : null}

      {useDtoolsBeautifiedLayout && dtoolsLiveViewModel ? (
        <DtoolsLiveProposalContent
          model={dtoolsLiveViewModel}
          audience={isInternalPreview ? "staff" : "customer"}
          contentLead={
            <>
              {approvedReadOnlyBanner}
              {cancelledAcceptedBanner}
              {revisionBanner}
              <ProposalApprovedVariationOrders
                items={
                  (
                    data as {
                      approvedVariationOrders?: import("@shared/variationOrderPresentation").ApprovedVoHistorySummary[];
                    }
                  ).approvedVariationOrders ?? []
                }
                staffLinks={isInternalPreview}
              />
            </>
          }
          customerPricing={customerPricing}
          retailPricingJson={retailPricing}
          totalAmount={
            dtoolsLiveViewModel.variationOrderScope?.revisedContractValue ?? data.header.totalAmount
          }
          pdfPackageMeta={{
            revisionNumber: (data.header as { revisionNumber?: number | null }).revisionNumber,
            proposalDate: (data.header as { createdAt?: string | Date | null }).createdAt,
            proposalStatus: data.header.status,
            approvedAt: (data.header as { approvedAt?: string | Date | null }).approvedAt,
            linkBUrl: customerPublicUrl,
          }}
          optionActions={{
            proposalId: id,
            proposalRef: data.header.proposalRef,
            customerEmail: (data as { customer?: { email?: string | null } }).customer?.email ?? null,
            customerPricing,
            proposalStatus: data.header.status,
            // Approval always requires a secure Link B credential, which this page never has
            // (staff preview and the Clarity One records viewer are both read-only here).
            approvalDisabled: isInternalPreview || isClarityOneCustomerView,
            salesperson: data.salesperson,
          }}
          onOpenCleo={showCleoAssistantUi ? () => setCleoOpen(true) : undefined}
        />
      ) : null}

      {dtoolsBeautifiedUnavailable ? (
        <DtoolsLiveProposalUnavailable
          proposalRef={data.header.proposalRef}
          audience={isInternalPreview ? "staff" : "customer"}
        />
      ) : null}

      {isPremiumStandardLayout ? (
        <RetailPremiumProposalContent
          cover={{
            customerName: data.customer?.name ?? "Valued Customer",
            proposalTitle: resolveRetailProposalTitle({
              proposalName: data.header.proposalName,
              proposalRef: data.header.proposalRef,
            }),
            proposalRef: data.header.proposalRef,
          }}
          proposalTitle={resolveRetailProposalTitle({
            proposalName: data.header.proposalName,
            proposalRef: data.header.proposalRef,
          })}
          items={items}
          customerIntroParagraph={
            isProposalCustomerCleoContextHidden(cleoAudience) || hideClarityIntroduction
              ? null
              : data.customerIntroParagraph
          }
          paymentTerms={paymentTerms}
          createdAt={data.header.createdAt}
          linkBExpiresAt={(data.header as { linkBExpiresAt?: string | Date | null }).linkBExpiresAt ?? null}
          notes={data.header.notes}
          tradeInItemsText={
            (data.header as { hasTradeInItems?: boolean; tradeInItemsText?: string | null }).hasTradeInItems
              ? (data.header as { tradeInItemsText?: string | null }).tradeInItemsText
              : null
          }
          finalAgreedPrice={customerPricing.finalAgreedPrice}
          originalPrice={customerPricing.proposalTotal}
          showFinalAgreedPrice={customerPricing.showFinalAgreedPrice}
          gstAmount={customerPricing.gstAmount}
          showGst={customerPricing.showGst}
          finalAmountPayable={customerPricing.finalAmountPayable}
          showFinalAmountPayable={customerPricing.showFinalAmountPayable}
          audience={isInternalPreview ? "staff" : "customer"}
          contentLead={
            <>
              {approvedReadOnlyBanner}
              {cancelledAcceptedBanner}
              {revisionBanner}
            </>
          }
          fullViewportCover={customerFullViewportCover}
          enableRetailLineHover={isCatalogueRetailProposal}
          onAskCleoAboutLine={
            showCleoAssistantUi
              ? ({ itemId, question }) => {
                  setCleoOpen(true);
                  setCleoPendingAsk({
                    key: Date.now(),
                    question,
                    proposalItemId: itemId,
                  });
                }
              : undefined
          }
          pricesVisibleToCustomer={resolveRetailPricesVisibleToCustomer(retailPricing)}
          approvalActions={{
            proposalId: id,
            proposalRef: data.header.proposalRef,
            customerName: (data as { customer?: { name?: string | null } }).customer?.name ?? null,
            customerEmail: (data as { customer?: { email?: string | null } }).customer?.email ?? null,
            customerContact:
              (data as { customer?: { contact?: string | null } }).customer?.contact ?? null,
            customerPricing,
            proposalStatus: data.header.status,
            approvedAt: (data.header as { approvedAt?: string | Date | null }).approvedAt,
            approvalDisabled: isInternalPreview || isClarityOneCustomerView,
            salesperson: data.salesperson,
          }}
        />
      ) : null}


      {showCleoAssistantUi ? (
        <ProposalPublicCleoAssistant
          proposalId={id}
          open={cleoOpen}
          onOpenChange={setCleoOpen}
          enabled={showCleoAssistantUi}
          audience={cleoAudience}
          pendingAsk={cleoPendingAsk}
          onPendingAskConsumed={() => setCleoPendingAsk(null)}
        />
      ) : null}
      {isInternalPreview && data?.header ? (
        <>
        {staffViewPdfRequested ? (
          <div className="fixed top-5 right-5 z-50 proposal-no-print">
            <ProposalPdfDownloadButton
              proposalId={id}
              proposalRef={data.header.proposalRef}
              skipAnalytics
              label="View PDF"
              delivery="open"
              autoStart
              onAutoStartSettled={() => {
                clearStaffViewPdfQueryFromLocation();
                setStaffViewPdfRequested(false);
              }}
              className="shadow-lg"
            />
          </div>
        ) : null}
        <div className="fixed bottom-5 left-5 z-40 flex max-w-md flex-col gap-2 proposal-no-print">
          <ProposalLinkAInternalPreviewBanner />
          {pipelineStage ? (
            <ProposalPipelineStageBadge pipelineStage={pipelineStage} className="w-fit shadow-sm" />
          ) : null}
          {data.header.projectId && String((data.header as any).originalProposalStorageKey ?? "").trim() ? (
            <a
              href={staffPrimaryProposalViewPath({
                id,
                projectId: data.header.projectId,
                originalProposalStorageKey: (data.header as any).originalProposalStorageKey,
              })}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-[#1A2744]/20 bg-white px-4 py-2 text-sm font-medium text-[#1A2744] shadow hover:bg-[#FAFAF7]"
            >
              Open Link B (original PDF wrapper) in new tab →
            </a>
          ) : null}
        </div>
        <div className="fixed bottom-5 right-20 z-40 proposal-no-print">
          <Button
            type="button"
            className="shadow-lg"
            disabled={markSent.isPending}
            onClick={() => setSendModalOpen(true)}
          >
            {markSent.isPending ? "Preparing..." : "Send to Customer for Approval"}
          </Button>
        </div>
        </>
      ) : null}

      <ProposalDraftPromotionDialog
        open={promotionOpen && isInternalPreview}
        onOpenChange={setPromotionOpen}
        pending={markSent.isPending}
        onConfirm={() => {
          void (async () => {
            const url = await ensureCustomerLinkReady(true);
            if (!url) return;
            setPromotionOpen(false);
            const action = pendingShareRef.current;
            pendingShareRef.current = null;
            if (action) await action(url);
            setSendModalOpen(false);
          })();
        }}
      />

      <Dialog open={sendModalOpen && isInternalPreview} onOpenChange={setSendModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send to Customer for Approval</DialogTitle>
            <DialogDescription>Choose how to share the customer proposal link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {customerPhoneDigits ? (
              <Button type="button" className="w-full justify-start" onClick={sendViaWhatsApp}>
                <MessageCircle className="mr-2 h-4 w-4" />
                WhatsApp
              </Button>
            ) : null}
            {customerEmail ? (
              <Button type="button" variant="outline" className="w-full justify-start" onClick={sendViaEmail}>
                <Mail className="mr-2 h-4 w-4" />
                Email
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={() => void copyCustomerLink()}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy Link
            </Button>
            <ProposalCustomerLinkShareField url={resolveShareUrl()} />
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setSendModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
